
import { useMemo } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { GroupUtils } from '../../../utils/GroupUtils';
import { useUserDetailsQuery } from './useUserDetailsQuery';
import { BRANDS_QUERY } from '../../queries/BRANDS_QUERY';
import cloneDeep from 'lodash/cloneDeep';

/**
 * A hooks used to retrieve the data on the brands a user is a part of.
 */
export function useUserBrandsQuery( options ) {
    const { data: brandsResponse, loading: brandsLoading, error: brandsError } = useQuery( BRANDS_QUERY, options );
    const { data: userDetailsResponse, loading: userDetailsLoading, error: userDetailsError, refetch } = useUserDetailsQuery();

    const isLoading = ( brandsLoading || userDetailsLoading );
    const isError = ( brandsError || userDetailsError );

    const userSegmentData = useMemo( () => {
        const userGroups = userDetailsResponse?.person?.groups || [];
        return GroupUtils.getSegmentGroups( userGroups );
    }, [ userDetailsResponse ] );

    const userSegmentBrandIds = useMemo( () => {
        const brandCollection = userSegmentData?.find( ( segmentCollection ) => ( segmentCollection?.name === 'brand' ) );
        return ( brandCollection?.ids || [] );
    }, [ userSegmentData ] );

    return useMemo ( () => {
        const response = ( userDetailsResponse && brandsResponse ) ? cloneDeep( brandsResponse ) : undefined;
        if ( response ) {
            let brandsList = brandsResponse?.brands?.edges;
            if ( brandsList && userDetailsResponse && userSegmentBrandIds ) {
                // Remove any brands from the data that the user isn't included in.
                response.brands.edges = brandsList?.filter( ( brandData ) => {
                    return userSegmentBrandIds?.includes( brandData?.node?.segmentId );
                } );
            }
        }

        return {
            error: isError,
            loading: isLoading,
            data: response,
            refetch,
        };
    }, [ isError, isLoading, brandsResponse, refetch, userSegmentBrandIds, userDetailsResponse ] );
}
